<template>
    <div class="p-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
        <button
          @click="$router.push('/config')"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Configuration Preview
        </button>
        Approve binary
      </h1>
  
      <div v-if="loading" class="text-center py-4">
        <p class="text-gray-600">Loading...</p>
      </div>

      <!-- Change Binary Modal -->
      <div v-if="changingItem">
        <div 
          @click.self="changingItem=false"  
          class="items-center justify-center inset-0 absolute bg-gray-600 bg-opacity-50 flex">
          <div class="dark:bg-gray-900 bg-white p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 w-[60%] h-[45%] m-auto shadow-xl items-center text-center border-gray">
            <div class="mb-4 text-base flex space-x-2 justify-between">
              <span class="font-semibold">Are you sure you want to change this binary?</span>
              <button
                @click="changingItem=false"
                class="hover:bg-white dark:hover:bg-gray-900"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <div class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded">
              <p class="font-semibold mt-2">This change will affect your workspace configurations and may impact:</p>
              <ul class="my-3">
                <li class="font-medium justify-left flex pl-4">Active data collection processes.</li>
                <li class="font-medium justify-left flex pl-4">Linked workspace, launcher and collector configurations.</li>
              </ul>
            </div>
            <div class="flex justify-center space-x-4">
              
              <div class=" text-base flex space-x-2 justify-between">
                <button 
                  class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="proceedWithFileUpload">
                  Accept
                </button>
                <button 
                  class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  @click="changingItem=false">
                  Cancel
                </button>
              </div>
            </div>
          </div>  
        </div>
      </div>
  
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
  
      <form v-else @submit.prevent="saveBinary" class="space-y-6 p-6 glass-card">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              for="license_state"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Binary Type</label
            >
            <GlassSelect
              id="organization"
              v-model="formField.binary_type"
              :options="binaryFormatting"
              placeholder="Select a Binary Type"
              :valueKey="'key'"
              :labelKey="'label'"
              :disabled="true"
              :error="!!fieldErrors['license_state']"
            />
            <FieldError :message="fieldErrors['license_state']"/>
          </div>

          <div>
            <label
              for="organization"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Organisation</label
            >
            <GlassSelect
              id="organization"
              v-model="formField.organization"
              :options="orgs"
              placeholder="Select an Organisation"
              :disabled="true"
              :error="!!fieldErrors['organization']"
            />
            <FieldError :message="fieldErrors['organization']"/>
         </div>

         <div>
            <label
              for="group"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent Group</label
            >
            <GlassSelect
              id="group"
              v-model="formField.group"
              :options="groups"
              placeholder="Select an Agent Group"
              :disabled="true"
              :error="!!fieldErrors['group']"
            />
            <FieldError :message="fieldErrors['group']"/>
         </div>

         <div>
            <label
              for="agent"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent</label
            >
            <GlassSelect
              id="agent"
              v-model="formField.agent"
              :options="agents"
              placeholder="Select an Agent"
              :valueKey="'licensekey'"
              :labelKey="'licensekey'"
              :disabled="true"
              :error="!!fieldErrors['agent']"
            />
            <FieldError :message="fieldErrors['agent']"/>
            <FieldError :message="fieldErrors['non_field_errors']"/>
         </div>


         <div>
            <label for="file"  class="block text-sm font-medium text-gray-700 dark:text-white">
              Outgoing File
            </label>

            <div v-if="formField.file">
              <a 
                :href="formField.file" 
                target="_blank" 
                download
               class="glass-card block px-4 py-2 rounded border border-blue-600 dark:text-gray-50 text-black truncate"
              >
                Outgoing: {{ formField.file?.name || formField.file }}
              </a>
            </div>

          </div>

          <div>
            <label for="file"  class="block text-sm font-medium text-gray-700 dark:text-white">
              Incoming File
            </label>

            <div v-if="formField.staged_file">
              <a 
                :href="formField.staged_file" 
                target="_blank" 
                download
                class="glass-card block px-4 py-2 rounded border border-gray-900 text-black dark:text-gray-50 truncate"
              >
                Incoming: {{ formField.staged_file?.name || formField.staged_file }}
              </a>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            class="mt-5 text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            @click="denyFile"
          >
            <div class="flex space-x-2">
              <p class="font-medium">Deny</p>
            </div>  
          </button>

          <div class="relative group">
            <button
            type="button"
            class="mt-5 text-white focus:ring-4 focus:outline-none focus:ring-green-300 dark:focus:ring-green-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            @click="approveFile"
          >
            <div class="flex space-x-2">
              <p class="font-medium">Approve Binary</p>
            </div>  
          </button> 
          <div class="absolute bottom-full mb-2 right-0 hidden group-hover:block">
            <div class="glass-card p-6">
              Warning: This action is permanent and cannot be undone
            </div>
          </div> 
          </div>
          
        </div>
      </form>
    </div>
  </template>
  
  <script>
  import { createBinary, getBinarysById, updateBinary, updateFile, approveBinary, denyBinary } from "@/services/binaryService";
  import { getOrgs } from "@/services/wsOrgService";
  import { getAgentGroups } from "@/services/agentGroupService";
  import { getAgents } from "@/services/agentService";
  import { useStore } from "@/store";
  import FieldError from "@/components/FieldError.vue";
  import GlassSelect from "@/components/GlassSelect.vue";

  export default {
    name: "OrganizationDetails",
    components: {
      FieldError,
      GlassSelect
    },
    props: {
      binaryId:{
        type: Number,
      },
    },
    data() {
      return {
        formField: {
          binary_type: "",
          checksum: "",
          file: null,
          organization: null,
          group: null,
          agent: null,
          approved: false,
          status: "initial",
          staged_file: ""
        },
        loading: false,
        error: null,
        fieldErrors: {},
        isNewBinary: false,
        orgs: [],
        agents: [],
        groups: [],
        binary_types : ['workspace' , 'launcher', 'collector'],
        canChangeBinary: false,
        canAddBinary: false,
        changingItem: false,
        fileReference: null,
      };
    },
    computed: {
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      formattedBinary() {
        return {
          ...this.formField
        };
      },
      binaryFormatting(){
        return this.binary_types.map(item => ({
            key : item,
            label: `${item[0].toUpperCase()}${item.slice(1)}`
        }));
      }
    },
    watch: {
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
    },
    async created() {
      const binaryId = this.binaryId;
      this.checkPermissions();
      
      if (binaryId === "new") {
        this.isNewBinary = true;
      } else {
        await this.fetchBinary(binaryId);
      }
      await this.fetchOrg();
      await this.fetchAgents();
      await this.fetchGroups();
    },
    methods: {
      checkPermissions(){
        this.canChangeBinary = useStore.hasPermission("Can change binaries");
        this.canAddBinary = useStore.hasPermission("Can add binaries");
      },
      
      async approveFile(){
        try {
          await approveBinary(this.formField.id);
          this.$router.push({ name: "ConfigView" });
        } catch (error) {
          console.error(error)
        }
      },
      async denyFile(){
        try {
          await denyBinary(this.formField.id);
          this.$router.push({ name: "ConfigView" });
        } catch (error) {
          console.error(error)
        }
      },
      async fetchOrg(){
        try {
          this.orgs = await getOrgs();
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      },
      async fetchAgents(){
        try {
          this.agents = await getAgents();
        } catch (error) {
          console.error("Error fetching agents:", error);
        }
      },
      async fetchGroups(){
        try {
          this.groups = await getAgentGroups();
        } catch (error) {
          console.error("Error fetching groups:", error);
        }
      },
      async fetchBinary(binaryId) {
        this.loading = true;
        this.error = null;
        try {
          this.formField =  await getBinarysById(binaryId);
        } catch (error) {
          console.error("Error fetching binary:", error);
          this.error =
            "An error occurred while fetching binary details. Please try again later.";
        } finally {
          this.loading = false;
        }
      },
    },
  };
  </script>
  